import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDataProvider } from "react-admin";
import DataTable from "react-data-table-component";
import { setProductSearch } from "../../Reducers/General";
import { useDispatch, useSelector } from "react-redux";
import FilterComponent from "./FilterComponent";
import {
  ProductImage,
  Container,
  Content,
  HeaderRow,
  Title,
  Actions,
  TableCard,
  PriceList,
  PriceItem,
  PriceInput,
  PriceFeedback,
  StatusBadge,
  CreateButton,
  InlineEditToggle,
  PriceControlRow,
  PriceSpinner,
  PriceLabel,
  PriceValue,
} from "./style";

type InlinePriceStatusEntry = {
  status: "saving" | "success" | "error";
  message?: string;
};
const Dashboard = (props: any) => {
  const dispatch = useDispatch();
  const searchProduct = useSelector(
    (state: any) => state.general.productSearch
  );

  const [data, setData] = useState<any>([]);
  const [store, setStore] = useState<any>({});
  const [priceStatus, setPriceStatus] = useState<
    Record<string, InlinePriceStatusEntry>
  >({});
  const [inlineEditingEnabled, setInlineEditingEnabled] = useState(false);
  const priceInputsRef = useRef<Record<string, HTMLInputElement | null>>({});
  const dataProvider = useDataProvider();

  const getData = React.useCallback(() => {
    dataProvider
      .getOne("configuration", {})
      .then((res: any) => {
        if (res) setStore(res.data);
      })
      .catch((error: any) => {});

    dataProvider
      .getList("product", {
        pagination: { page: 1, perPage: 1000 },
        sort: { order: "ASC", field: "id" },
      })
      .then((res: any) => {
        if (res) setData(res.data);
        //console.log("res", res.data);
      })
      .catch((error: any) => {
        //console.log(error);
      });
  }, [dataProvider, setData]);

  const filteredItems = data?.filter(
    (item) =>
      item.name &&
      item.name.toLowerCase().includes(searchProduct?.toLowerCase())
  );

  const getPriceKey = (productId: number, priceIndex: number) =>
    `${productId}-${priceIndex}`;

  const formatPriceForInput = (value: number) =>
    (value / 100).toFixed(2).replace(".", ",");

  const normalizePriceInput = (value: string) => {
    if (!value) return "";
    let normalized = value.replace(/\s/g, "");
    const hasComma = normalized.includes(",");
    const hasDot = normalized.includes(".");
    if (hasComma && hasDot) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (hasComma) {
      normalized = normalized.replace(",", ".");
    }
    return normalized;
  };

  const maskPriceInputValue = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      return "";
    }

    const integerDigits = digits.length > 2 ? digits.slice(0, -2) : "0";
    const cents = digits.slice(-2).padStart(2, "0");
    const normalizedInteger = integerDigits === "" ? "0" : parseInt(integerDigits, 10).toString();

    return `${normalizedInteger},${cents}`;
  };

  const handlePriceInputTyping = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    target.value = maskPriceInputValue(target.value);
  };

  const orderedPriceKeys = useMemo(
    () =>
      filteredItems?.flatMap((item: any) =>
        (item.prices ?? []).map((_: any, index: number) =>
          getPriceKey(item.id, index)
        )
      ) ?? [],
    [filteredItems]
  );

  const registerPriceInput = useCallback(
    (key: string) => (node: HTMLInputElement | null) => {
      if (node) {
        priceInputsRef.current[key] = node;
      } else {
        delete priceInputsRef.current[key];
      }
    },
    []
  );

  const handleTabNavigation = (currentKey: string, reverse: boolean) => {
    if (!inlineEditingEnabled) return;
    const keys = orderedPriceKeys;
    if (!keys.length) return;
    const currentIndex = keys.indexOf(currentKey);
    if (currentIndex === -1) return;

    let nextIndex = reverse ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0) {
      nextIndex = keys.length - 1;
    } else if (nextIndex >= keys.length) {
      nextIndex = 0;
    }

    const nextKey = keys[nextIndex];
    const nextInput = priceInputsRef.current[nextKey];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const handlePriceInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      const currentKey = event.currentTarget.dataset.priceKey;
      if (currentKey) {
        handleTabNavigation(currentKey, event.shiftKey);
      }
    }
  };
  const updateStatus = (key: string, entry?: InlinePriceStatusEntry) => {
    setPriceStatus((prev) => {
      const next = { ...prev };
      if (entry) {
        next[key] = entry;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const handleInlinePriceEdit = (
    value: string,
    row: any,
    priceItem: any,
    priceIndex: number
  ) => {
    const key = getPriceKey(row.id, priceIndex);
    const normalized = normalizePriceInput(value);

    if (normalized === "") {
      updateStatus(key, {
        status: "error",
        message: "Informe um valor",
      });
      return;
    }

    const parsed = parseFloat(normalized);
    if (Number.isNaN(parsed)) {
      updateStatus(key, {
        status: "error",
        message: "Valor inválido",
      });
      return;
    }

    if (parsed < 0) {
      updateStatus(key, {
        status: "error",
        message: "Valor deve ser positivo",
      });
      return;
    }

    const newValue = Math.round(parsed * 100);
    if (newValue === priceItem.value) {
      updateStatus(key);
      return;
    }

    const previousValue = priceItem.value;
      updateStatus(key, { status: "saving" });
    const optimisticPrices = row.prices.map((price: any, idx: number) =>
      idx === priceIndex ? { ...price, value: newValue } : price
    );

    setData((prev: any) =>
      prev.map((productEntry: any) =>
        productEntry.id === row.id
          ? {
              ...productEntry,
              prices: optimisticPrices,
            }
          : productEntry
      )
    );

    dataProvider
      .update("product", {
        id: row.id,
        data: {
          ...row,
          prices: optimisticPrices,
        },
      })
      .then(() => {
        updateStatus(key, { status: "success", message: "✔" });
        setTimeout(() => updateStatus(key), 1800);
      })
      .catch((error: any) => {
        setData((prev: any) =>
          prev.map((productEntry: any) =>
            productEntry.id === row.id
              ? {
                  ...productEntry,
                  prices: productEntry.prices.map((price: any, idx: number) =>
                    idx === priceIndex
                      ? { ...price, value: previousValue }
                      : price
                  ),
                }
              : productEntry
          )
        );
        updateStatus(key, {
          status: "error",
          message:
            error?.message ??
            "Não foi possível salvar. Verifique a conexão e tente novamente.",
        });
      });
  };

  useEffect(() => {
    getData();
  }, [getData]);
  //console.log("log", props);

  const columns = [
    {
      name: "Fotos",
      selector: "photos",
      sortable: false,
      compact: true,
      cell: (row) => {
        return (
          <ProductImage
            resizeMode="contain"
            src={
              row?.photos[0]?.thumbnailUrl ??
              require("../../Assets/images/no-image.png")
            }
          />
        );
      },
    },
    {
      name: "Nome",
      selector: "name",
      sortable: true,
      compact: true,
    },

    {
      name: "Categoria",
      selector: "category.name",
      sortable: true,
      hide: "sm" as const,
      compact: true,
    },
    {
      name: "Preço",
      selector: "price",
      sortable: false,
      cell: (row: any) => {
        if (!row.prices?.length) {
          return <span>—</span>;
        }

        return (
          <PriceList>
            {row.prices.map((item: any, index: number) => {
              const key = getPriceKey(row.id, index);
              const feedback = priceStatus[key];
              const label = item.title ?? "";
              const currencyLabel = store?.currency ? `${store.currency}` : "";
              const formattedValue = formatPriceForInput(item.value);

              return (
                <PriceItem key={`${item.title ?? "preco"}-${index}`}>
                  {label && <PriceLabel>{label}</PriceLabel>}
                  {!inlineEditingEnabled ? (
                    <PriceValue>
                      {currencyLabel && `${currencyLabel} `}
                      {formattedValue}
                    </PriceValue>
                  ) : (
                    <PriceControlRow>
                      <span>{currencyLabel || "R$"}</span>
                      <PriceInput
                        key={`${key}-${item.value}`}
                        type="text"
                        defaultValue={formattedValue}
                        inputMode="decimal"
                        data-price-key={key}
                        ref={registerPriceInput(key)}
                        onBlur={(event) =>
                          handleInlinePriceEdit(
                            event.target.value,
                            row,
                            item,
                            index
                          )
                        }
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onInput={handlePriceInputTyping}
                        onKeyDown={handlePriceInputKeyDown}
                      />
                      {feedback?.status === "saving" && <PriceSpinner />}
                      {feedback?.message && feedback?.status !== "saving" && (
                        <PriceFeedback status={feedback.status}>
                          {feedback.message}
                        </PriceFeedback>
                      )}
                    </PriceControlRow>
                  )}
                </PriceItem>
              );
            })}
          </PriceList>
        );
      },
    },
    {
      name: "Ativo",
      selector: "isActive",
      sortable: true,
      hide: "sm" as const,
      compact: true,
      cell: (row) => {
        return (
          <StatusBadge active={row.isActive}>
            {row.isActive ? "Ativo" : "Inativo"}
          </StatusBadge>
        );
      },
    },
    {
      name: "Ações",
      selector: "isActive",
      sortable: true,
      hide: "sm" as const,
      compact: true,
      cell: (row) => {
        return "Editar";
      },
    },
  ];

  const handleChange = (row) => {
    window.location.href = `/admin#/product/${row.id}`;
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: 700,
        color: "#616161",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        backgroundColor: "#fafafa",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
      },
    },
    cells: {
      style: {
        paddingTop: "12px",
        paddingBottom: "12px",
      },
    },
  };

  return (
    <Container>
      <Content>
        <HeaderRow>
          <Title>Produtos</Title>
          <Actions>
            <InlineEditToggle
              type="button"
              onClick={() => setInlineEditingEnabled((prev) => !prev)}
            >
              {inlineEditingEnabled
                ? "Parar edição de valores"
                : "Edição rápida de valores"}
            </InlineEditToggle>
            <FilterComponent
              onFilter={(e: any) => {
                dispatch(setProductSearch(e.target.value));
              }}
              filterText={searchProduct}
            />
            <CreateButton href="#/product/create">+ Criar produto</CreateButton>
          </Actions>
        </HeaderRow>

        <TableCard>
          <DataTable
            columns={columns}
            data={filteredItems}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            striped
            pointerOnHover
            highlightOnHover={true}
            noDataComponent="Nenhum produto encontrado"
            paginationComponentOptions={{
              rowsPerPageText: "Itens por página:",
              rangeSeparatorText: "de",
              noRowsPerPage: false,
              selectAllRowsItem: false,
              selectAllRowsItemText: "Todos",
            }}
            noHeader
            customStyles={customStyles}
            onRowClicked={inlineEditingEnabled ? undefined : handleChange}
          />
        </TableCard>
      </Content>
    </Container>
  );
};

export default Dashboard;
